import React, { useEffect, useReducer, useRef } from "react";
import { FormattedMessage, injectIntl } from "react-intl";
import { getRemotedbUrl, i18nIntl, isEmpty, isNotEmpty } from "../../lib/ep";

import ActionLink from "../../components/builder/ActionLink";
import ButtonCollection from "../../components/builder/ButtonCollection";
import DisabledArea from "../../components/builder/DisabledArea";
import ImageUploader from "../../components/builder/ImageUploader";
import Input from "../../components/builder/Input";
import SaveButton from "../../components/builder/SaveButton";
import TextArea from "../../components/builder/TextArea";
import LeveledHTMLText from "../../components/front/helpers/LeveledHTMLText";
import ProgressBar from "../../components/builder/ProgressBar";
import ActionButton from "../../components/builder/ActionButton";
import EmailField from "../../components/builder/EmailField";
import ToggleContent from "../../components/Form/ToggleContent";
import FloatingLayerPublic from "../../components/front/FloatingLayerPublic";
import md5 from "md5";
import ImageCropper from "../../components/builder/ImageCropper";
import PdfEmbed from "../../components/front/PdfEmbed";

const getMimeType = (base64) => {
	const parts = base64.split(';');
	const mime = parts[0].split(':');
	const mimetype = mime[1];
	return mimetype;
}

const Wrapper = (props) => {
	const description = (isNotEmpty(props, 'description')) ? props.description : false;

	return (
		<>
			{(description !== false) && (
				<article class="template_module gui_main_text_color_on_bg gui_not_in_bottom">
					<LeveledHTMLText>{description}</LeveledHTMLText>
				</article>
			)}

			<div style={{ width:'100%' }}>
				{props.children}
			</div>
		</>
	)
};

const TicketViewLayer = ({ image, ...props }) => {
	return (
		<FloatingLayerPublic
			backButton={ToggleContent.useCloseAction()}
			pm={props.promotion}
			_class={{ size:'small' }}
		>
			<div>
				<img
					src={image}
					width="100%"
					alt=""
				/>
			</div>
		</FloatingLayerPublic>
	)
}
export const default_waiting_sentences = [
	{ text:"Uploading the image" },
	{ text:"Resizing the image" },
	{ text:"Improving image" },
	{ text:"Contacting with AI" },
	{ text:"Processing AI" },
	{ text:"Getting AI response" },
	{ text:"Checking conditions" },
	{ text:"Ending process" },
];

const OcrTicketProcess = ({ stage, button_label, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const errors = {}
	const has_errors = false;
	const is_builder = props?.is_builder ?? false;

	const processing = useRef(0);
	const ticket = useRef(null);
	const process_init = useRef(0);
	const process_duration = useRef(0);

	const max_time = 45;	// in seconds
	const max_calls = Math.floor(max_time/2);

	const waiting_sentences = stage?.reqs_waiting_sentences ?? default_waiting_sentences;

	const loop_waiting_sentences = stage?.reqs_loop_waiting_sentences ?? false;
	const waiting_sentence_delay = 3;	// in seconds
	const success_redirect_delay = 3;	// in seconds

	const displayMessage = () => {
		fetch(getRemotedbUrl(`/stages/prereq_ocr_status/${stage.promotion_id}/${ticket.current}`)).then((r) => r.json()).then((r) => {
			if(r.status == 1) {
				const duration = (Date.now() - process_init.current) / 1000;	// in seconds
				process_duration.current = duration / max_time;
	
				dispatch({
					type:'SET_STATE',
					state:'SUCCESS',
					errors:false,
					response:r,
				});
			}

			if(r.status == 2) {
				dispatch({
					type:'SET_STATE',
					state:'ERROR',
					errors:r.errors,
				});
			}

			if(r.status == 3) {
				dispatch({
					type:'SET_STATE',
					state:'PREMODERATION',
				});
			}
			
			if(r.status == 9) {
				dispatch({
					type:'SET_STATE',
					state:'ERRORS',
					errors:r.errors,
				});
			}

			if(r.status == 4) {
				dispatch({
					type:'SET_STATE',
					state:'USER_FEEDBACK',
					errors:r.errors,
				});
			}
		});

		if(loop_waiting_sentences || processing.current < waiting_sentences.length) {
			let new_sentence_id;
			if(loop_waiting_sentences) {
				new_sentence_id = processing.current % waiting_sentences.length;
			} else {
				new_sentence_id = processing.current;
			}

			dispatch({
				type:'CHANGE',
				k:'message',
				v:`[ ${i18n(waiting_sentences[new_sentence_id].text)} ]`,
			});
		}

		setTimeout(() => {
			if(processing.current == -1) return;

			processing.current++;
			if(processing.current > max_calls) {
				processing.current = -1;
				dispatch({ type:'SET_STATE', state:'TIME_EXCEEDED' });
				return;
			}
			displayMessage();
		}, (waiting_sentence_delay * 1000));
	};

	const startProcessing = (t) => {
		ticket.current = t;
		processing.current = 0;
		process_init.current = Date.now();

		dispatch({
			type:'SET_STATE',
			state:'PROCESSING',
			ticket:t,
		});

		setTimeout(() => {
			displayMessage();
		}, 500);
	};

	const change = (k, v) => {
		if(k == 'full_image' || is_builder) return;
		dispatch({ type:'CHANGE', k, v });
		if (k == 'image') {
			dispatch({ type:'CHANGE', k:'hash',v:md5(v)});
		}else if (k == 'exif') {
			dispatch({ type:'CHANGE', k:'exif',v});
		}
		dispatch({ type:'CHANGE', k:'crop',v:null});
	};

	const submitTicket = (e, cb) => {
		if(is_builder) {
			cb();
			return;
		}

		fetch(getRemotedbUrl(`/stages/prereq_ocr_ticket/${stage.promotion_id}`), {
			method:'POST',
			headers:{
				'Content-Type':'application/json',
			},
			body:JSON.stringify({
				image:state?.crop ?? state.image,//NO crop => PDF
				stage_id:stage.id,
				hash:state.hash,
				exif:state.exif
			}),
		}).then((r) => r.json()).then((r) => {
			if(r.status != 1) {
				dispatch({
					type:'SET_STATE',
					state:'ERROR',
					errors:r.errors,
				});
				return;	// error
			}

			startProcessing(r.ocr_ticket);
			if(cb != undefined) cb();
		});
	};

	const [ state, dispatch ] = useReducer((state, action) => {
		switch(action.type) {
			case 'SET_STATE':
				if(['PREMODERATION', 'SUCCESS', 'ERROR', 'USER_FEEDBACK', 'ERRORS', 'TIME_EXCEEDED'].includes(action.state)) {
					processing.current = -1;
				}

				if(action?.ticket != undefined) {
					state.ticket = action.ticket;
				}

				if(action?.errors != undefined) {
					state.errors = action.errors;
				}

				if(action.state == 'SUCCESS') {
					state.response = action.response;
				}

				if(action.state == 'UPLOAD_IMAGE') {
					state.image = null;
					state.crop = null;
					state.hash = null
					state.exif = null
					processing.current = 0;
				}

				return {
					...state,
					state:action.state,
				};

			case 'CHANGE':
				return {
					...state,
					[action.k]:action.v,
				};
			
			case 'CHANGE_FEEDBACK':
				return {
					...state,
					feedback:{ 
						...state.feedback, 
						[action.error_id]:{ ...state.feedback[action.error_id], [action.k]:action.v } }
				};
		}

		return state;
	}, {
		state:'UPLOAD_IMAGE',
		image:null,
		crop:null,
		hash:null,
		exif:null,
		ocr_ticket:false,
		feedback:{},
		message:'',
		errors:false,
		response:{},
		has_email:(props.notification_email != "") ? true : false,
		email_valid:(props.notification_email != "") ? true : false,
		email:props.notification_email
	});

	useEffect(() => {
		process_init.current = Date.now();

		if (props?.ocr_ticket?.StageOcrTicket) {
			if (props?.ocr_ticket?.StageOcrTicket.status == 4) {
				dispatch({
					type:'SET_STATE',
					state:'USER_FEEDBACK',
					ticket:props?.ocr_ticket?.StageOcrTicket.id,
					errors:props?.ocr_ticket?.StageOcrTicket.metadata.conditions,
				});
			}
		}
		//dispatch({ type:'SET_STATE', state:'PREMODERATION' });
	}, []);

	if(props.remaining_ocr <= 0) {
		return (
			<Wrapper
				description={<>
					<h1>{i18n("We're sorry, the receipt validation system is currently unavailable.")}</h1>
					<p>{i18n("Please try again later.")}</p>
				</>}
			/>
		)
	}

	const set_notification_email = (e, cb) => {
		fetch(getRemotedbUrl(`/stages/prereq_ocr_notify/${stage.promotion_id}`), {
			method:'POST',
			headers:{
				'Content-Type':'application/json',
			},
			body:JSON.stringify({
				ocr_ticket_id:state.ticket,
				email:state.email,
			}),
		}).then((r) => r.json()).then((r) => {
			if(r.status == 0 && r.reason == 'already_done') {
				dispatch({
					type:'SET_STATE',
					state:'SUCCESS',
					errors:false,
					response:{ status:1, ticket:r.ticket },
				});
				return;
			}
			
			if(r.status == 0 && r.reason == 'feedback') {
				dispatch({
					type:'SET_STATE',
					state:'USER_FEEDBACK',
					errors:r.errors,
				});
				return;
			}

			dispatch({
				type:'SET_STATE',
				state:'WE_LL_CALL_YOU',
			})
		});

		if(cb != undefined) cb();
	};

	const changeFeedback = (error_id, k, v) => {
		dispatch({
			type:'CHANGE_FEEDBACK',
			error_id,
			k,
			v,
		});
	};

	const sendFeedback = (e, cb) => {
		fetch(getRemotedbUrl(`/stages/prereq_ocr_feedback/${stage.promotion_id}`), {
			method:'POST',
			headers:{
				'Content-Type':'application/json',
			},
			body:JSON.stringify({
				ocr_ticket_id:state.ticket,
				email:state.email,
				feedback:Object.keys(state.feedback).map((k) => ({ condition_id:k, feedback:state.feedback[k] })),
			}),
		}).then((r) => r.json()).then((r) => {
			dispatch({
				type:'SET_STATE',
				state:'PREMODERATION',
			})
		});

		if(cb != undefined) cb();
	};

	const deleteTicket = () => {
		fetch(getRemotedbUrl(`/stages/prereq_ocr_feedback/${stage.promotion_id}`), {
			method:'POST',
			headers:{
				'Content-Type':'application/json',
			},
			body:JSON.stringify({
				ocr_ticket_id:state.ticket,
				delete:true
			}),
		}).then((r) => r.json()).then((r) => {
			dispatch({
				type:'SET_STATE',
				state:'UPLOAD_IMAGE',
			});
		});
	};

	if(state.state == 'PROCESSING') {
		return (
			<Wrapper
				description={<><h1>{i18n("Processing the receipt")}</h1><p>{i18n("This can take up to 30 seconds.")}</p></>}
			>
				<div style={{ maxWidth:'320px', width:'100%', margin:'0 auto' }}>
					<ProgressBar
						key='progress_bar'
						actual={0}
						max={100}
						color='mainBg whiteColor'
						size="big"
						display_percent={false}
						display_percent_in_bar={false}
						animation_duration={`${max_time}s`}
						initial_width='0%'
						classes="partial_animation"
						onEnd={() => {
							if(processing.current <= 0) return;
							processing.current = -1;
							dispatch({ type:'SET_STATE', state:'TIME_EXCEEDED' });
						}}
					/>

					<p
						class="progress_msg gui_not_in_top_tiny"
						style={{ '--default-font-size':'0.8rem' }}
					>{state.message}</p>

					{/*<ButtonCollection
						_class={{ other:'gui_not_in_top' }}
					>
						<ActionLink
							onClick={() => {
								dispatch({ type:'SET_STATE', state:'PREMODERATION' })
								if(props.validate != undefined) props.validate({}, () => {})
						}}>Call me later</ActionLink>
					</ButtonCollection>*/}
				</div>
			</Wrapper>
		)
	}

	if(['ERROR', 'ERRORS'].includes(state.state)) {
		let error_title = "";
		let error_subtitle = "";
		let error_msg = "";

		switch(state.state) {
			case 'ERROR':
				// maybe this is not in use anymore (and replaced with 'ERRORS' => 'duplicated_ticket')
				error_title = "This receipt has already been used";
				error_msg = "Please try with a different one.";
				break;

			case 'ERRORS':
				error_title = "The receipt is not valid";
				error_msg = "Unexpected error";

				switch(state.errors[0]) {
					case 'unique_ticket':
					case 'same_date_login_id':
						error_title = "This receipt has already been registered";
						error_msg = "Please review the instructions and upload a different receipt.";
						break;

					case 'duplicated_ticket':
						error_title = "This receipt has already been used";
						error_msg = "Please try with a different one.";
						break;

					case 'store_name_mandatory':
						error_subtitle = "We couldn't detect the store name in the image.";
						error_msg = "Please make sure you upload a photo of the full receipt, without margins and clearly readable. Upload another receipt to continue.";
						break;

					case 'handwrite_detected':
						error_msg = "Handwritten text detected. Only printed receipts are accepted. Please upload a new receipt.";
						break;

					case 'date_mandatory':
						error_subtitle = "We couldn't detect the date in the image.";
						error_msg = "Please make sure you upload a photo of the full receipt, without margins and clearly readable. Upload another receipt to continue.";
						break;

					case 'total_mandatory':
						error_subtitle = "We couldn't detect the total amount in the image.";
						error_msg = "Please make sure you upload a photo of the full receipt, without margins and clearly readable. Upload another receipt to continue.";
						break;

					case 'ticket_not_readable':
						error_msg = "The ticket is not readable or too long to be processed.";
						break;
				}
				break;
		}

		return (
			<Wrapper
				description={<>
					<h1>{i18n(error_title)}</h1>
					{(error_subtitle != "") ? <p><strong>{i18n(error_subtitle)}</strong></p> : <></>}
					<p>{i18n(error_msg)}</p></>}
			>
				<div class="gui_flexbox_auto gui_flex_col gui_not_in_top_big">
					<ButtonCollection
						_class={{ other:'flex_button gui_big_button gui_not_in_bottom_small' }}
					>
						<ActionButton
							_fullclass=""
							onClick={() => {
								dispatch({
									type:'SET_STATE',
									state:'UPLOAD_IMAGE',
								});
							}}
						>Try again</ActionButton>
					</ButtonCollection>
				</div>
			</Wrapper>
		)
	}

	if(state.state == 'SUCCESS') {
		const final_animation_duration = 0.7;	// in seconds

		return (
			<Wrapper
				description={<><h1>{i18n("The receipt is valid")}</h1><p>{i18n("You will be automatically redirected.")}</p></>}
			>
				<div style={{ maxWidth:'320px', width:'100%', margin:'0 auto' }}>
					<ProgressBar
						key='finished_bar'
						actual={100 * process_duration.current}
						max={100}
						color='whiteColor'
						size="big"
						display_percent={false}
						display_percent_in_bar={false}
						animation_duration={`${final_animation_duration}s`}
						initial_width={`${100 * process_duration.current}%`}
						classes="partial_animation forced_value"
						reinforcement_color='var(--ribbon-open-bg-color-rgb)'
						extra_contents={<div class="finish_icon">
							<span class="ep-check-list-item whiteIcon" />
						</div>}
						onEnd={() => {
							setTimeout(() => {
								document.querySelector('.finish_icon').classList.add('display_animation');
							}, (final_animation_duration * 1000));

							setTimeout(() => {
								if(props.validate != undefined) props.validate(state.response);
							}, (success_redirect_delay * 1000))
						}}
					/>

					<p
						class="progress_msg gui_not_in_top_tiny"
						style={{ '--default-font-size':'0.8rem' }}
					>[ {i18n("The receipt is valid")} ]</p>
				</div>
			</Wrapper>
		)
	}

	if(state.state == 'TIME_EXCEEDED') {
		return (
			<Wrapper
				description={<>
					<h1>{i18n("This is taking too long")}</h1>

					<p>{i18n("You'll receive an email notification once your receipt has been processed.")}</p>
				</>}
			>
				<EmailField
					model={state}
					change={(k, v) => dispatch({ type:'CHANGE', k, v })}
					disabled={state.has_email} 
					onValidate={(is_valid) => dispatch({ type:'CHANGE', k:'email_valid', v:is_valid })}
					name="email"
				>Notification email</EmailField>

				<ButtonCollection
					_class={{ other:'flex_button gui_big_button gui_not_in_bottom_small' }}
				>
					<SaveButton
						disabled={!state.email_valid}
						onClick={set_notification_email}
					>Confirm</SaveButton>
				</ButtonCollection>
			</Wrapper>
		)
	}

	if(state.state == 'WE_LL_CALL_YOU') {
		return (
			<Wrapper
				description={<>
					<h1>{i18n("Stay tuned!")}</h1>
					<p>{i18n("You'll receive an email notification once your receipt has been processed.")}</p>
				</>}
			/>
		)
	}

	if(state.state == 'PREMODERATION') {
		return (
			<Wrapper
				description={<>
					<LeveledHTMLText>
						<h2>{i18n("Thank you, we'll manually review the information provided as soon as possible.")}</h2>
						<p>{i18n("We will let you know shortly if the receipt is valid.")}</p>
					</LeveledHTMLText>
					<ButtonCollection
						_class={{ other:'footer_actions gui_not_in_top_big' }}
					>	
						<ActionLink
							_href={`/p/${stage.promotion_id}`}
						>Home</ActionLink>
					</ButtonCollection>
				</>}
			/>
		)
	}

	if(state.state == 'USER_FEEDBACK') {
		const unique_errors = [];
		const added_errors = [];
		state.errors.map((_error) => {
			const error = stage.reqs_ocrticket_conditions.find((c) => (c.id == _error.id));
			if(error == undefined) return;
			if(added_errors.includes(error.type)) return;

			added_errors.push(error.type);
			unique_errors.push(error);
		});

		return (
			<Wrapper
				description={<><h1>{i18n("Your receipt will be manually reviewed")}</h1><p>{i18n("We couldn't automatically validate the information in your receipt. Please, give us more details so we can manually validate and manage your participation correctly.")}</p></>}
			>
				<div>
					<ToggleContent>
						<ToggleContent.EditButton>
							<ActionLink>View uploaded receipt</ActionLink>
						</ToggleContent.EditButton>

						<ToggleContent.Edit>
							<TicketViewLayer
								image={state.image ?? props.ocr_ticket?.StageOcrTicket?.metadata?.image_url}
								promotion={props.promotion}
							/>
						</ToggleContent.Edit>
					</ToggleContent>

					<ul class="errors_user_feedback gui_not_in_top_huge">
						{unique_errors.map((error, e) => {
							let error_label;
							let field_label = "Enter it here";
							let InputType = Input;
							switch(error.type) {
								case 'store':
									error_label = "We were unable to validate the store name.";
									break;

								case 'location':
									error_label = "We were unable to validate the store location.";
									break;

								case 'article':
								case 'amount_article':
								case 'quantity_article':
									error_label = "We were unable to validate the mandatory products.";
									field_label = "Enter the item, quantity and amount here";
									InputType = TextArea;
									break;

								case 'ticket_dates':
									error_label = "We were unable to validate the receipt date.";
									break;

								case 'ticket_total':
									error_label = "We were unable to validate the receipt total amount.";
									break;
							}

							const field_styles = {
								//'--field-label-text-transform':'none',
							};
							if(e > 0) field_styles.marginBlockStart = '2em';

							return (
								<li key={`error_${error.id}`} style={field_styles}>
									<p class="ocr_error_label">
										<strong>{i18n(error_label)}</strong>
									</p>

									<InputType
										name="feedback"
										change={(k, v) => changeFeedback(error.id, k, v)}
										_class={{ first:true }}
									>{field_label}</InputType>
								</li>
							)
						})}

						{!state.has_email && (
							<EmailField
								name="email"
								model={state}
								onValidate={(is_valid) => dispatch({ type:'CHANGE', k:'email_valid', v:is_valid })}
								change={(k, v) => dispatch({ type:'CHANGE', k, v })}
							>Notification email</EmailField>
						)}
					</ul>

					<div class="gui_flexbox_auto gui_flex_col gui_not_in_top_big" style={{ gap:'0.5em' }}>
						<ButtonCollection
							_class={{ other:'flex_button gui_big_button gui_not_in_bottom_small' }}
						>
							<SaveButton
								disabled={!state.email_valid}
								_fullclass=""
								onClick={sendFeedback}
							>Send</SaveButton>
						</ButtonCollection>

						<ActionLink
							onClick={deleteTicket}
						>Delete and start over</ActionLink>

						{props.back_link}
					</div>
				</div>
			</Wrapper>
		)
	}
	const is_pdf = isNotEmpty(state, 'image') && getMimeType(state.image) == 'application/pdf' ? true : false;
	if(isNotEmpty(state, 'image') && isEmpty(state, 'crop') && !is_pdf) {
		return (
			<Wrapper
				description={stage.reqs_description}
			>
				<div
					style={{
						width:'80svh',
						height:'80svh',
						zIndex:'99',
						position:'fixed',
						top:'10svh',
						left:'calc(50vw - 80svh / 2)',
					}}
				>
					<ImageCropper
						image={state.image}
						exif={state.exif}
						width={0.8 * window.innerHeight}
						height={0.8 * window.innerHeight}
						//footer_message={`${i18n("Make sure only the receipt is visible before continuing.")}<br /><strong>${i18n("Images with margins or background may be rejected.")}</strong>`}
						crop_mandatory_msg={<>
							<h1>{i18n("Instructions")}</h1>

							<ol style={{ '--text-ol-padding-start':'1em' }}>
								<li>
									<FormattedMessage
										id="Crop the edges of the image so that {highlight}."
										values={{
											highlight:<strong>{i18n("only the receipt is visible, without any background")}</strong>,
										}}
									/>
								</li>

								<li>
									<FormattedMessage
										id="Make sure {highlight} is readable."
										values={{
											highlight:<strong>{i18n("the store name")}</strong>,
										}}
									/>
								</li>

								<li>
									<FormattedMessage
										id="Make sure {highlight} is readable."
										values={{
											highlight:<strong>{i18n("the total amount")}</strong>,
										}}
									/>
								</li>
							</ol>
						</>}
						onCrop={(image) => {
							dispatch({ type:'CHANGE', k:'crop', v:image });
							dispatch({ type:'CHANGE', k:'image', v:image });
						}}
						onCancel={() => {
							dispatch({ type:'SET_STATE', state:'UPLOAD_IMAGE' });
						}}
						promotion={props.promotion}
					/>
				</div>
			</Wrapper>
		)
	}

	const default_instructions_placeholder = `<p>${i18n("The photo of the receipt must meet these requirements")}:</p><ul><li>${i18n("Only one receipt appears.")}</li><li>${i18n("The whole receipt appears.")}</li><li>${i18n("All content is readable.")}</li><li>${i18n("The receipt is not wrinkled or folded.")}</li><li>${i18n("The receipt has no stains.")}</li><li>${i18n("The reverse of the receipt is not visible. Avoid backlit photos.")}</li><li>${i18n("Make sure the receipt is in focus.")}</li></ul>`;

	const disabled_button = (has_errors || state.image == undefined) ? true : false;
	let disabled_button_msg = "";
	if(disabled_button) {
		disabled_button_msg = (has_errors) ? "To submit, please first fix the errors" : "You need to upload a ticket first"
	}

	if(isNotEmpty(state, 'image') && is_pdf){
		return (
			<Wrapper
				description={stage.reqs_description}
			>
				<div>
					<div 
						class="react_field" 
						style={{
							aspectRatio:'1 / 1',
							border:'1px dashed rgba(var(--main-color), 1)',
							cursor:'pointer',
						}}
						onClick={()=>dispatch({ type:'CHANGE', k:'image', v:null })}
					>
						<PdfEmbed
							full_file={state.image}
							type='pdf'
							skip_actions={true}
						/>
					</div>

					<div class="flex_button gui_big_button gui_flex_center gui_not_in_top_big">
						<DisabledArea
							disabled={disabled_button}
							message={disabled_button_msg}
						>
							<SaveButton
								_fullclass=""
								disabled={disabled_button}
								onClick={submitTicket}
								autotranslate={false}
							>{button_label}</SaveButton>
						</DisabledArea>
					</div>

					<div
						class="ocr_instructions"
						dangerouslySetInnerHTML={{
							__html:(isNotEmpty(stage, 'reqs_ocr_instructions')) ? stage.reqs_ocr_instructions : default_instructions_placeholder,
						}}
					/>
				</div>
			</Wrapper>
		)
	}

	return (
		<Wrapper
			description={stage.reqs_description}
		>
			<div>
				<ImageUploader
					_class={{ first:true, other:'big_uploader' }}
					name="image"
					errors={errors}
					image={state.image}
					change={change}
					min_weight={{size:40,error:i18n("The image is too small. Please try again with another one where the receipt is clearer")}}
					onReadExif={(exif) => change('exif',exif)}
					exif={state.exif}
					dd_size="full_w"
					display_custom_dragging_instructions="Upload a receipt"
					preview={true}
					is_builder={is_builder}
					small_input={false}
					enable_actions={false}
					_resize={{ width:'original', height:'original' }} // we need to pass "_resize" to enable EXIF rotation compensation
					componentConfig = {{
						iconFiletypes:['.jpg', '.png', '.gif', '.webp','.pdf'],
						showFiletypeIcon:true,
						showFiletypeIcon:false,
					}}
					allowed = {{
						'image/jpeg':1,
						'image/gif':1,
						'image/png':1,
						'image/webp':1,
						'application/pdf':1,
					}}
				>{stage?.reqs_code_image_label ?? "Upload an image of the receipt"}</ImageUploader>

				<div class="flex_button gui_big_button gui_flex_center gui_not_in_top_big">
					<DisabledArea
						disabled={disabled_button}
						message={disabled_button_msg}
					>
						<SaveButton
							_fullclass=""
							disabled={disabled_button}
							onClick={submitTicket}
							autotranslate={false}
						>{button_label}</SaveButton>
					</DisabledArea>
				</div>

				<div
					class="ocr_instructions"
					dangerouslySetInnerHTML={{
						__html:(isNotEmpty(stage, 'reqs_ocr_instructions')) ? stage.reqs_ocr_instructions : default_instructions_placeholder,
					}}
				/>
			</div>
		</Wrapper>
	)
};

export default injectIntl(OcrTicketProcess);